import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowDown,
  ArrowUp,
  MinusCircle,
  PlusCircle,
  RefreshCw,
  Trash2,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  deleteCasSnapshot,
  fetchCasSnapshotDiff,
  fetchCasSnapshots,
  fetchPortfolioTimeline,
} from "../lib/api";
import { normalizeDetail } from "../lib/errors";

const INR = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

function inr(n) {
  if (n == null) return "—";
  return `₹${INR.format(Math.round(Number(n) || 0))}`;
}

function inrCompact(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(1)}Cr`;
  if (Math.abs(v) >= 1_00_000) return `₹${Math.round(v / 1_00_000)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${Math.round(v)}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function shortMonth(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
}

// Factory returns a stable render fn for recharts <Area dot={...} />. Recharts calls this
// with the point props (cx/cy/payload). Extracted so React's no-unstable-nested-components
// rule stays happy.
function makeDotRenderer(selectedId) {
  return function TimelineDot(props) {
    const { cx, cy, payload } = props;
    const isSelected = payload?.id === selectedId;
    return (
      <circle
        key={payload?.id}
        cx={cx}
        cy={cy}
        r={isSelected ? 6 : 4}
        stroke="#F4C64B"
        strokeWidth={2}
        fill={isSelected ? "#F4C64B" : "#0f0e0c"}
        style={{ cursor: "pointer" }}
      />
    );
  };
}

/**
 * Portfolio Timeline — plots the total portfolio value across every CAS import and lets the
 * user drill into a snapshot-vs-previous diff (per-scheme units + NAV movement, plus split
 * between new-money contribution and price-movement P&L).
 */
export function PortfolioTimelinePage() {
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [snaps, tl] = await Promise.all([
        fetchCasSnapshots(),
        fetchPortfolioTimeline(),
      ]);
      setSnapshots(snaps);
      setTimeline(tl);
      // Default-select the newest snapshot so users see the latest diff first.
      if (snaps.length) {
        setSelectedId((prev) => prev || snaps[snaps.length - 1].id);
      } else {
        setSelectedId(null);
        setDiff(null);
      }
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't load your portfolio."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!selectedId) return;
    setDiffLoading(true);
    fetchCasSnapshotDiff(selectedId)
      .then(setDiff)
      .catch((err) => toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't load diff.")))
      .finally(() => setDiffLoading(false));
  }, [selectedId]);

  async function handleDelete(id) {
    if (!window.confirm("Delete this snapshot? The historical timeline point will disappear.")) return;
    try {
      await deleteCasSnapshot(id);
      toast.success("Snapshot deleted.");
      // Reset selection if it was the one we deleted.
      setSelectedId((prev) => (prev === id ? null : prev));
      await refresh();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't delete."));
    }
  }

  const chartData = useMemo(
    () =>
      timeline.map((s) => ({
        ...s,
        label: shortMonth(s.statement_to),
      })),
    [timeline],
  );

  const first = timeline[0];
  const last = timeline[timeline.length - 1];
  const totalGrowth = last && first ? last.total_value - first.total_value : 0;
  const totalGrowthPct = first && first.total_value > 0 ? (totalGrowth / first.total_value) * 100 : null;
  // "Total invested" = net new money added across every consecutive CAS pair, derived from
  // unit-delta × NAV (works for both CAMS and NSDL/CDSL demat). Baseline snapshot contributes
  // 0 — we start counting only from the second CAS onward. Sign follows cash flow so a period
  // of net redemption reduces the total.
  const totalInvested = timeline.reduce(
    (sum, s) => sum + (Number(s.net_invested_this_period) || 0),
    0,
  );
  // Market P&L compares the growth to only the money added AFTER the baseline snapshot —
  // the first snapshot's own SIPs are already baked into `first.total_value`, so subtracting
  // them again would give a misleadingly negative number.
  const postBaselineInvested = Number(last?.cumulative_invested) || 0;
  // Demat CAS (NSDL/CDSL) are typically summary-only — no per-transaction ledger, so
  // `purchased_this_period` / `redeemed_this_period` will be 0 even when units moved.
  // Surface a small note so users don't misread the "Total invested" figure.
  const hasDematSnapshot = snapshots.some((s) => {
    const t = (s.cas_type || "").toLowerCase();
    return t.includes("nsdl") || t.includes("cdsl") || t.includes("depository") || t.includes("demat");
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-8" data-testid="portfolio-timeline-page">
      <button type="button" onClick={() => navigate(-1)} className="text-xs text-muted hover:text-gold" data-testid="page-back">← Go back</button>
      <div className="mt-2 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-serif text-3xl text-text sm:text-4xl">
            Portfolio <span className="text-gold">Timeline</span>
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted">
            Every CAS you import becomes a dated snapshot. We deduplicate holdings by ISIN + folio
            and split each period&apos;s move into new-money contribution vs price-movement P&amp;L.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="timeline-refresh">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Link to="/app/net-worth">
            <Button size="sm" data-testid="timeline-import-cta">Import a CAS</Button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 text-center text-sm text-muted">Loading your portfolio…</div>
      ) : snapshots.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <TimelineStats
            latest={last}
            earliest={first}
            totalGrowth={totalGrowth}
            totalGrowthPct={totalGrowthPct}
            totalInvested={totalInvested}
            postBaselineInvested={postBaselineInvested}
            snapshotCount={snapshots.length}
            hasDematSnapshot={hasDematSnapshot}
          />
          <TimelineChart data={chartData} onPointClick={setSelectedId} selectedId={selectedId} />
          <SnapshotsList
            snapshots={snapshots}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onDelete={handleDelete}
          />
          {selectedId && (
            <DiffSection diff={diff} loading={diffLoading} />
          )}
        </>
      )}
    </div>
  );
}

/* ---------- Empty state ---------- */

function EmptyState() {
  return (
    <Card className="mt-8 border-gold/25">
      <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
        <Wallet className="h-10 w-10 text-gold" />
        <h2 className="font-serif text-xl text-text">No CAS imported yet</h2>
        <p className="max-w-md text-sm text-muted">
          Import your Consolidated Account Statement (from CAMS or NSDL) and we&apos;ll start plotting
          your monthly portfolio value here. Each subsequent CAS unlocks a diff — units bought,
          NAV movement, gain/loss.
        </p>
        <Link to="/app/net-worth">
          <Button className="mt-2" data-testid="timeline-import-empty">Import your first CAS</Button>
        </Link>
      </CardContent>
    </Card>
  );
}

/* ---------- Stat cards ---------- */

function TimelineStats({ latest, earliest, totalGrowth, totalGrowthPct, totalInvested, postBaselineInvested, snapshotCount, hasDematSnapshot }) {
  const positive = totalGrowth >= 0;
  const marketPnl = totalGrowth - postBaselineInvested;
  return (
    <>
    <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="timeline-stats">
      <StatCard
        label="Latest portfolio value"
        value={inr(latest?.total_value)}
        sub={`As of ${formatDate(latest?.statement_to)}`}
      />
      <StatCard
        label="Since first import"
        value={inr(earliest?.total_value)}
        sub={formatDate(earliest?.statement_to)}
      />
      <StatCard
        label="Total invested"
        value={`${totalInvested >= 0 ? "+" : ""}${inrCompact(totalInvested)}`}
        sub="Net new money added since your first CAS (baseline)"
        tone={totalInvested > 0 ? "positive" : undefined}
      />
      <StatCard
        label="Market P&L"
        value={`${marketPnl >= 0 ? "+" : "-"}${inrCompact(Math.abs(marketPnl))}`}
        sub={
          totalGrowthPct != null
            ? `Gain ${positive ? "+" : ""}${totalGrowthPct.toFixed(2)}% overall`
            : "—"
        }
        tone={marketPnl >= 0 ? "positive" : "negative"}
      />
      <StatCard
        label="Snapshots on file"
        value={String(snapshotCount)}
        sub="Chronologically ordered"
      />
    </div>
    {hasDematSnapshot && (
      <p
        className="mt-2 text-xs text-muted"
        data-testid="timeline-demat-note"
      >
        <span className="font-medium text-amber-300">Note:</span> Depository CAS (NSDL/CDSL)
        are summary statements — they don&apos;t itemize buys/sells for the period, so any
        cash-in on demat holdings won&apos;t be counted in <em>Total invested</em>. Mutual-fund
        CAS (CAMS/KFin) are unaffected.
      </p>
    )}
    </>
  );
}

function StatCard({ label, value, sub, tone }) {
  const toneClass = tone === "positive" ? "text-emerald-300" : tone === "negative" ? "text-red-400" : "text-text";
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-4">
        <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
        <div className={`font-serif text-2xl ${toneClass}`}>{value}</div>
        <div className="text-xs text-muted">{sub}</div>
      </CardContent>
    </Card>
  );
}

/* ---------- Chart ---------- */

function TimelineChart({ data, onPointClick, selectedId }) {
  if (data.length === 0) return null;
  // Zoom the Y-axis to just below the smallest snapshot so month-to-month changes are
  // actually visible instead of a nearly-flat line pinned near the top of a 0-baseline axis.
  const values = data.map((r) => Number(r.total_value) || 0).filter((v) => v > 0);
  const yMin = values.length ? Math.min(...values) : 0;
  const yMax = values.length ? Math.max(...values) : 0;
  const range = yMax - yMin;
  const useZoomedAxis = range > 0 && yMin > range * 5;
  const yDomain = useZoomedAxis ? [yMin - range * 0.25, yMax + range * 0.15] : ["auto", "auto"];
  return (
    <Card className="mt-6" data-testid="timeline-chart-card">
      <CardContent className="p-4 sm:p-6">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-serif text-lg text-text">Value over time</h3>
          <span className="text-xs text-muted">Click a point to see that period&apos;s diff</span>
        </div>
        <div className="h-64 sm:h-80 relative">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={data}
              margin={{ top: 10, right: 12, left: -8, bottom: 0 }}
              onClick={(e) => {
                const p = e?.activePayload?.[0]?.payload;
                if (p?.id) onPointClick(p.id);
              }}
            >
              <defs>
                <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F4C64B" stopOpacity={0.55} />
                  <stop offset="95%" stopColor="#F4C64B" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" tick={{ fill: "#a5a29c", fontSize: 12 }} />
              <YAxis
                tick={{ fill: "#a5a29c", fontSize: 12 }}
                tickFormatter={inrCompact}
                domain={yDomain}
                allowDataOverflow={false}
              />
              <Tooltip
                cursor={{ stroke: "#F4C64B", strokeWidth: 1 }}
                contentStyle={{
                  background: "#1a1917",
                  border: "1px solid rgba(244,198,75,0.4)",
                  borderRadius: 8,
                }}
                labelStyle={{ color: "#e4dfd0", fontWeight: 600 }}
                formatter={(val, name) => {
                  if (name === "total_value") return [inr(val), "Value"];
                  return [val, name];
                }}
              />
              <Area
                type="monotone"
                dataKey="total_value"
                stroke="#F4C64B"
                strokeWidth={2}
                fill="url(#pv)"
                dot={makeDotRenderer(selectedId)}
                activeDot={{ r: 6, fill: "#F4C64B" }}
              />
            </AreaChart>
          </ResponsiveContainer>
          {useZoomedAxis && (
            <div
              className="pointer-events-none absolute left-2 bottom-8 flex items-center gap-1 text-[10px] font-mono text-muted/70"
              data-testid="timeline-axis-break"
              title="Y-axis is zoomed to the data range — ₹0 baseline is not shown"
            >
              <span className="text-base leading-none">⌇</span>
              <span>axis zoomed</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------- Snapshot list ---------- */

function SnapshotsList({ snapshots, selectedId, onSelect, onDelete }) {
  return (
    <div className="mt-6" data-testid="snapshot-list">
      <h3 className="font-serif text-lg text-text">All snapshots</h3>
      <div className="mt-3 grid gap-2">
        {[...snapshots].reverse().map((s) => {
          const delta = s.delta_from_prev;
          const pct = s.delta_from_prev_pct;
          const positive = delta != null && delta >= 0;
          const isSelected = s.id === selectedId;
          return (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(s.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(s.id);
                }
              }}
              className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors ${
                isSelected ? "border-gold bg-gold/5" : "border-border bg-card hover:border-gold/40"
              }`}
              data-testid={`snapshot-row-${s.id}`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className={`h-10 w-1 flex-shrink-0 rounded-full ${isSelected ? "bg-gold" : "bg-transparent"}`} />
                <div className="min-w-0">
                  <div className="text-sm text-text">
                    {formatDate(s.statement_to)}
                    {s.statement_to_source === "import_date_fallback" && (
                      <span className="ml-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-amber-300">
                        approximate
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted">
                    {s.cas_type || "CAS"} · {s.holdings_count} holdings · Cost {inrCompact(s.total_cost)}
                    {(s.net_invested_this_period || 0) !== 0 && (
                      <>
                        {" · "}
                        <span className="text-emerald-300">
                          Invested this period +{inrCompact(s.net_invested_this_period)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end">
                <div className="font-serif text-lg text-text">{inr(s.total_value)}</div>
                {delta != null && (
                  <div className={`flex items-center gap-1 text-xs ${positive ? "text-emerald-300" : "text-red-400"}`}>
                    {positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                    {positive ? "+" : ""}{inrCompact(delta)}
                    {pct != null && ` (${positive ? "+" : ""}${pct.toFixed(2)}%)`}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(s.id);
                }}
                className="ml-2 rounded-md p-1 text-muted hover:text-red-400"
                aria-label="Delete snapshot"
                data-testid={`snapshot-delete-${s.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Diff section ---------- */

function DiffSection({ diff, loading }) {
  if (loading) {
    return <div className="mt-8 text-center text-sm text-muted">Loading diff…</div>;
  }
  if (!diff) return null;
  const positive = (diff.total_delta || 0) >= 0;
  const hasPrev = !!diff.prev_snapshot;
  return (
    <div className="mt-8" data-testid="diff-section">
      <h3 className="font-serif text-lg text-text">
        {hasPrev
          ? `Change since ${formatDate(diff.prev_snapshot.statement_to)}`
          : "First snapshot — no previous statement to diff against"}
      </h3>

      {hasPrev && (
        <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="diff-totals">
          <StatCard
            label="Total change"
            value={`${positive ? "+" : ""}${inrCompact(diff.total_delta)}`}
            sub={
              diff.total_delta_pct != null
                ? `${positive ? "+" : ""}${diff.total_delta_pct.toFixed(2)}% MoM`
                : "—"
            }
            tone={positive ? "positive" : "negative"}
          />
          <StatCard
            label="New-money contribution"
            value={`+${inrCompact(diff.purchase_contribution || 0)}`}
            sub="SIPs + fresh purchases at new NAV"
            tone="positive"
          />
          <StatCard
            label="Price-movement P&L"
            value={`${(diff.price_movement_pnl || 0) >= 0 ? "+" : ""}${inrCompact(
              diff.price_movement_pnl || 0,
            )}`}
            sub="Mark-to-market on existing units"
            tone={(diff.price_movement_pnl || 0) >= 0 ? "positive" : "negative"}
          />
        </div>
      )}

      <DiffTable
        title="Changed positions"
        icon={TrendingUp}
        rows={diff.changed}
        columns={["scheme", "units", "nav", "value", "delta"]}
      />

      <DiffTable
        title="Added positions"
        icon={PlusCircle}
        rows={diff.added.map((a) => ({
          ...a,
          new_units: a.units,
          new_nav: a.nav,
          new_value: a.value,
        }))}
        columns={["scheme", "new_units", "new_nav", "new_value", "delta"]}
        emptyLabel="No new positions this period."
      />

      <DiffTable
        title="Removed / redeemed positions"
        icon={MinusCircle}
        rows={diff.removed}
        columns={["scheme", "units", "value", "delta"]}
        emptyLabel="No positions redeemed this period."
        tone="negative"
      />
    </div>
  );
}

function DiffTable({ title, icon: Icon, rows, columns, emptyLabel, tone }) {
  const has = rows && rows.length > 0;
  return (
    <div className="mt-6" data-testid={`diff-table-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mb-2 flex items-center gap-2 text-sm text-text">
        <Icon className={`h-4 w-4 ${tone === "negative" ? "text-red-400" : "text-gold"}`} />
        <span className="font-medium">{title}</span>
        <span className="text-xs text-muted">({rows?.length || 0})</span>
      </div>
      {!has ? (
        <p className="rounded-md border border-dashed border-border bg-card/40 p-4 text-center text-xs text-muted">
          {emptyLabel || "No changes."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[640px] text-left text-xs">
            <thead className="bg-elevated/40 text-[10px] uppercase tracking-widest text-muted">
              <tr>
                <th className="px-3 py-2">Scheme</th>
                {columns.includes("units") && <th className="px-3 py-2 text-right">Units (prev → new)</th>}
                {columns.includes("new_units") && <th className="px-3 py-2 text-right">Units</th>}
                {columns.includes("nav") && <th className="px-3 py-2 text-right">NAV (prev → new)</th>}
                {columns.includes("new_nav") && <th className="px-3 py-2 text-right">NAV</th>}
                {columns.includes("value") && <th className="px-3 py-2 text-right">Value</th>}
                {columns.includes("new_value") && <th className="px-3 py-2 text-right">Value</th>}
                {columns.includes("delta") && <th className="px-3 py-2 text-right">Δ Value</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => {
                const isChanged = "prev_units" in r;
                const deltaPositive = (r.delta_value || 0) >= 0;
                return (
                  <tr key={`${r.isin || r.scheme}-${i}`} className="hover:bg-elevated/30">
                    <td className="px-3 py-2">
                      <div className="text-text">{r.scheme}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted">
                        {r.amc || r.category || ""} {r.folio_masked ? `· Folio ${r.folio_masked}` : ""}
                      </div>
                    </td>
                    {isChanged && columns.includes("units") && (
                      <td className="px-3 py-2 text-right font-mono text-text/90">
                        {(r.prev_units || 0).toFixed(3)} → {(r.new_units || 0).toFixed(3)}
                        {r.unit_delta != null && (
                          <span className={`ml-1 ${r.unit_delta >= 0 ? "text-emerald-300" : "text-red-400"}`}>
                            ({r.unit_delta >= 0 ? "+" : ""}{r.unit_delta.toFixed(3)})
                          </span>
                        )}
                      </td>
                    )}
                    {!isChanged && columns.includes("units") && (
                      <td className="px-3 py-2 text-right font-mono text-text/90">
                        {(r.units || 0).toFixed(3)}
                      </td>
                    )}
                    {columns.includes("new_units") && (
                      <td className="px-3 py-2 text-right font-mono text-text/90">
                        {(r.new_units || 0).toFixed(3)}
                      </td>
                    )}
                    {isChanged && columns.includes("nav") && (
                      <td className="px-3 py-2 text-right font-mono text-text/90">
                        ₹{(r.prev_nav || 0).toFixed(2)} → ₹{(r.new_nav || 0).toFixed(2)}
                      </td>
                    )}
                    {columns.includes("new_nav") && (
                      <td className="px-3 py-2 text-right font-mono text-text/90">
                        ₹{(r.new_nav || 0).toFixed(2)}
                      </td>
                    )}
                    {columns.includes("value") && !isChanged && (
                      <td className="px-3 py-2 text-right font-mono text-text/90">
                        {inrCompact(r.value)}
                      </td>
                    )}
                    {isChanged && columns.includes("value") && (
                      <td className="px-3 py-2 text-right font-mono text-text/90">
                        {inrCompact(r.prev_value)} → {inrCompact(r.new_value)}
                      </td>
                    )}
                    {columns.includes("new_value") && (
                      <td className="px-3 py-2 text-right font-mono text-text/90">
                        {inrCompact(r.new_value)}
                      </td>
                    )}
                    {columns.includes("delta") && (
                      <td
                        className={`px-3 py-2 text-right font-mono ${
                          deltaPositive ? "text-emerald-300" : "text-red-400"
                        }`}
                      >
                        {deltaPositive ? "+" : ""}{inrCompact(r.delta_value)}
                        {isChanged && (
                          <div className="text-[10px] font-normal normal-case tracking-normal text-muted">
                            new money +{inrCompact(r.purchase_contribution || 0)} · price {(r.price_movement_pnl || 0) >= 0 ? "+" : ""}{inrCompact(r.price_movement_pnl || 0)}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PortfolioTimelinePage;
